import { Injectable } from '@angular/core';
import { Actions, ofType } from '@ngrx/effects';
import { ROUTER_NAVIGATED, RouterNavigatedAction } from '@ngrx/router-store';
import { ActivatedRouterStateSnapshot, CmsService, createFrom, EventService, ProductActions } from '@spartacus/core';
import { SearchProducts } from '@spartacus/core/src/product/store/actions/product-search.action';
import { Observable } from 'rxjs';
import { filter, map, tap, withLatestFrom } from 'rxjs/operators';
import { CustomCleanEcommerceEvent } from '../common/custom-common.events';
import { EcommerceType } from '../tag-manager-feature.model';
import { CustomNavigationEvent, CustomSearchEvent } from './custom-navigation.event';
import { UserAccountFacade } from '@spartacus/user/account/root';

/**
 * A builder for the NavigationEvent
 */
@Injectable({
  providedIn: 'root',
})
export class CustomNavigationEventBuilder {
  constructor(
    protected actions: Actions,
    protected eventService: EventService,
    protected cmsService: CmsService,
    protected userService: UserAccountFacade,
  ) {
    this.register();
  }

  /**
   * Registers events
   */
  protected register(): void {
    this.registerNavigationEvent();
    this.registerSearchEvent();
  }

  protected registerNavigationEvent(): void {
    this.eventService.register(CustomNavigationEvent, this.buildNavigationEvent());
  }

  protected registerSearchEvent(): void {
    this.eventService.register(CustomSearchEvent, this.buildSearchEvent());
  }

  /**
   * Builds the navigation events
   */
  protected buildNavigationEvent(): Observable<CustomNavigationEvent> {
    return this.getNavigatedEvent().pipe(
      withLatestFrom(this.cmsService.getCurrentPage()),
      map(([state, page]) => {
          return createFrom(CustomNavigationEvent, {
            event: 'navigation',
            context: state.context,
            semanticRoute: state.semanticRoute,
            url: state.url,
            params: state.params,
          })
        }
      ),
    );
  }

  protected buildSearchEvent(): Observable<CustomSearchEvent> {
    return this.actions.pipe(
      ofType(ProductActions.SEARCH_PRODUCTS),
      filter((action: SearchProducts) => Boolean(action.payload.queryText)),
      tap(() => this.eventService.dispatch(<CustomCleanEcommerceEvent>{ ecommerce: null }, CustomCleanEcommerceEvent)),
      withLatestFrom(this.userService.get()),
      map(([action, user]) => createFrom(CustomSearchEvent, {
        event: EcommerceType.SEARCH,
        userGroupId: user?.exclusiveRole || '',
        search_term: action.payload.queryText,
      }))
    );
  }

  private getNavigatedEvent(): Observable<ActivatedRouterStateSnapshot> {
    return this.actions.pipe(
      ofType<RouterNavigatedAction<ActivatedRouterStateSnapshot>>(
        ROUTER_NAVIGATED,
      ),
      map((event) => event.payload.routerState),
    );
  }
}
